import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
// Relative, not "@/…": this component is mounted by App.js and is therefore rendered by
// every App-level test, and the "@" alias does not exist under Jest.
import { captureFromLocation, refFromSearch } from "../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
import { capturePromotionFromLocation, promotionRefFromSearch } from "../lib/promotionRef";
// --- crm-module END ---

/**
 * The whole of the browser's half of attribution: hand the `?ref=` this URL carries to
 * `captureFromLocation`, which asks the server to count the click and mint the referral
 * ticket, and stores the answer. Renders nothing.
 *
 * MOUNTED ONCE PER `AppRouter` BRANCH (App.js), beside `<ScrollToHash />` — the only spot
 * that covers tenant hosts and platform hosts exactly once. `routes/platformRoutes.jsx`
 * is a bare `<Route>` fragment and cannot host a component; `tenant/TenantSite.jsx` is
 * rendered by BOTH branches, so mounting there would double every click.
 *
 * A `?ref=` is a CODE (an affiliate's own link) or a TICKET (our cross-origin hop through
 * `marketplaceLinks.withRef`); `lib/affiliateRef.js` tells them apart by shape. A page
 * load with no referral costs no request at all.
 *
 * ONE POST PER DISTINCT `?ref=` VALUE per mount: the counter is a page-load side effect,
 * and a client-side navigation that keeps the query string must not inflate it. The POST
 * is fire-and-forget — nothing here is ever shown to a visitor who did not ask for it.
 */
export default function AffiliateRefCapture() {
  const { search } = useLocation();
  const pending = refFromSearch(search);
  const posted = useRef(new Set());

  useEffect(() => {
    if (!pending || posted.current.has(pending)) return;
    posted.current.add(pending);
    captureFromLocation(search);
  }, [pending, search]);

  // --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
  // A promotion id needs no server-fetched POLICY the way the referral window does
  // (see `promotionRef.js`'s module docstring), so it captures unconditionally, on
  // every render this URL carries one — no gate on a program having loaded. The
  // CLICK COUNT is a separate concern (`promotions.stats.clicks`), already fired by
  // the card itself (`FeaturedStrip`/`MarketplaceLearn`) at the moment it is
  // clicked; this effect only makes the id survive the navigation that follows.
  const pendingPromo = promotionRefFromSearch(search);
  useEffect(() => {
    if (!pendingPromo) return;
    capturePromotionFromLocation(search);
  }, [pendingPromo, search]);
  // --- crm-module END ---

  return null;
}
